import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Layout, Typography, Button, Table, Spin } from 'antd'
import { PlayCircleOutlined, PauseCircleOutlined, HeartOutlined, HeartFilled } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '../../utils/redux/store'
import { attemptGetPlaylist, Song } from '../../utils/redux/playlistSlice'
import {
  attemptAddPlaylistToFavs,
  attemptDeletePlaylistFromFavs,
} from '../../utils/redux/favsSlice'
import { useAudioPlayer } from '../../contexts/AudioPlayerContext'
import { apiBaseURL } from '../../utils/api'
import dayjs from 'dayjs'
import './Playlist.scss'

const { Content } = Layout
const { Title, Text } = Typography

export default function Playlist() {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation('playlist')
  const dispatch = useAppDispatch()
  const playlist = useAppSelector((state) => (id ? state.playlist.playlists[id] : undefined))
  const favPlaylists = useAppSelector((state) => state.favs.playlists)
  const { currentSong, isPlaying, playSong, playQueue, togglePlayPause, isLoading } = useAudioPlayer()

  const isFavorite = favPlaylists?.some((fav) => fav._id === id) || false

  useEffect(() => {
    if (id) {
      dispatch(attemptGetPlaylist(id))
    }
  }, [id, dispatch])

  const handlePlaySong = (song: Song) => {
    if (currentSong?._id === song._id) {
      togglePlayPause()
    } else if (playlist) {
      playSong(song, playlist.songs)
    }
  }

  const handlePlayAll = () => {
    if (playlist && playlist.songs.length > 0) {
      playQueue(playlist.songs, 0)
    }
  }

  const handleToggleFavorite = () => {
    if (id) {
      if (isFavorite) {
        dispatch(attemptDeletePlaylistFromFavs(id))
      } else {
        dispatch(attemptAddPlaylistToFavs(id))
      }
    }
  }

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '0:00'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  if (!playlist) {
    return (
      <div className="playlist-loading">
        <Spin size="large" />
      </div>
    )
  }

  const columns = [
    {
      title: '#',
      dataIndex: 'index',
      key: 'index',
      width: 60,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: t('songs'),
      dataIndex: 'title',
      key: 'title',
      render: (title: string, record: Song) => (
        <div>
          <div className="song-title">{title}</div>
          <Text type="secondary" className="song-artist">
            {record.artist}
          </Text>
        </div>
      ),
    },
    {
      title: t('duration'),
      dataIndex: 'duration',
      key: 'duration',
      width: 100,
      render: (duration: number) => formatDuration(duration),
    },
    {
      title: '',
      key: 'action',
      width: 80,
      render: (_: any, record: Song) => {
        const isCurrentSong = currentSong?._id === record._id
        const isCurrentPlaying = isCurrentSong && isPlaying
        
        return (
          <Button
            type="text"
            icon={isCurrentPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
            onClick={(e) => {
              e.stopPropagation()
              handlePlaySong(record)
            }}
            loading={isLoading && isCurrentSong}
          />
        )
      },
    },
  ]

  return (
    <Content className="playlist-content">
      <div className="playlist-container">
        <div className="playlist-header">
          <img
            src={`${apiBaseURL}/uploaded/playlists/${playlist.image}`}
            alt={playlist.title}
            className="playlist-cover"
          />
          <div className="playlist-info">
            <Title level={2}>{playlist.title}</Title>
            {playlist.description && (
              <Text className="playlist-description">
                {playlist.description.en || playlist.description.fr}
              </Text>
            )}
            <div className="playlist-meta">
              <Text type="secondary">
                {playlist.songsCount} {t('songs')}
              </Text>
              {playlist.totalDuration && (
                <>
                  <span className="separator">•</span>
                  <Text type="secondary">{formatDuration(playlist.totalDuration)}</Text>
                </>
              )}
              {playlist.updatedAt && (
                <>
                  <span className="separator">•</span>
                  <Text type="secondary">
                    {t('updatedAt')} {dayjs(playlist.updatedAt).format('MMM DD, YYYY')}
                  </Text>
                </>
              )}
            </div>
            <div className="playlist-actions">
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                size="large"
                onClick={handlePlayAll}
              >
                {t('playAll')}
              </Button>
              <Button
                icon={isFavorite ? <HeartFilled /> : <HeartOutlined />}
                size="large"
                onClick={handleToggleFavorite}
              />
            </div>
          </div>
        </div>

        <div className="playlist-table">
          <Table
            columns={columns}
            dataSource={playlist.songs}
            rowKey="_id"
            pagination={false}
            onRow={(record) => ({
              className: currentSong?._id === record._id ? 'playing-row' : '',
            })}
          />
        </div>
      </div>
    </Content>
  )
}
